import { useState } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useChartColors } from './theme';
import { useTooltip } from './ChartTooltip';
import TableView from './TableView';
import { axisPadLeft, formatPeriodLabel, labelEvery } from './axisPadding';
import { useChartWidth } from './useChartWidth';

export interface ColumnDatum { label: string; value: number; }
interface ColumnsProps {
    data: ColumnDatum[];
    height?: number;
    colorIndex?: number;
    yFmt?: (n: number) => string;
    /** Compact formatter for axis ticks only (tooltips/table keep the exact yFmt). */
    tickFmt?: (n: number) => string;
    marker?: { index: number; label: string };
    max?: number;
}

// Ported from docs/dashboard-redesign/mockups/assets/viz.js `columns`.
// niceMax picks a round axis top; bars are thin with a 4px rounded top anchored to the baseline.
function niceMax(max: number): number {
    if (max <= 0) return 1;
    const mults = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
    const pow = Math.pow(10, Math.floor(Math.log10(max)));
    for (const m of mults) if (pow * m >= max) return pow * m;
    return pow * 10;
}

export default function Columns({ data, height = 200, colorIndex = 0, yFmt, marker, max, tickFmt }: ColumnsProps) {
    const { t } = useTranslation('dashboard');
    const c = useChartColors();
    const { show, hide, node } = useTooltip();
    const [showTable, setShowTable] = useState(false);

    const [wrapRef, W] = useChartWidth();
    const H = height, padB = 26, padT = 10;
    const top = max !== undefined ? niceMax(Math.max(1, max)) : niceMax(Math.max(1, ...data.map(d => d.value)));
    const fmt = yFmt ?? ((n: number) => String(n));
    const tfmt = tickFmt ?? fmt;
    const padL = axisPadLeft([0, 0.25, 0.5, 0.75, 1].map(g => tfmt(top * g)), 11, yFmt ? 36 : 28);
    const plotH = H - padB - padT, plotW = W - padL - 8;
    const bw = Math.min(38, (plotW / data.length) * 0.62);
    const step = plotW / data.length;
    // 'YYYY-MM' categories read as 'Mar 2026' (short 'Mar 26' when columns are narrow); thin
    // the labels so neighbours never collide.
    const tickLabels = data.map(d => formatPeriodLabel(d.label, step < 60));
    const every = labelEvery(tickLabels, step, 11);
    const markerX = marker && marker.index >= 0 && marker.index < data.length
        ? padL + step * marker.index + step / 2
        : null;

    return (
        <Box ref={wrapRef}>
            <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: 'block', maxWidth: '100%' }} role="img" aria-label={t('charts.barChartAria')}>
                {[0, 0.25, 0.5, 0.75, 1].map((g, i) => {
                    const y = padT + plotH * (1 - g);
                    return (
                        <g key={i}>
                            <line x1={padL} y1={y} x2={W - 8} y2={y} stroke={c.grid} strokeWidth={1} />
                            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize={11} fill={c.muted}>{tfmt(top * g)}</text>
                        </g>
                    );
                })}
                {data.map((d, i) => {
                    const h = plotH * (d.value / top);
                    const x = padL + step * i + (step - bw) / 2;
                    const y = padT + plotH - h;
                    return (
                        <g key={i}
                           onMouseMove={e => show(e.clientX, e.clientY, `${formatPeriodLabel(d.label)}: ${fmt(d.value)}`)}
                           onMouseLeave={hide}>
                            <rect x={x} y={y} width={bw} height={Math.max(0, h)} rx={3} fill={c.series[colorIndex]} />
                            {i % every === 0 &&
                                <text x={x + bw / 2} y={H - 10} textAnchor="middle" fontSize={11} fill={c.muted}>{tickLabels[i]}</text>}
                        </g>
                    );
                })}
                {markerX !== null && marker && (
                    <g pointerEvents="none">
                        <line x1={markerX} y1={padT} x2={markerX} y2={padT + plotH} stroke={c.series[1]} strokeWidth={1.5} strokeDasharray="4 3" />
                        <text x={markerX} y={padT - 2} textAnchor="middle" fontSize={11} fill={c.series[1]}>{marker.label}</text>
                    </g>
                )}
            </svg>
            <Button size="small" onClick={() => setShowTable(s => !s)}>{t('charts.tableView')}</Button>
            {showTable && <TableView columns={[t('charts.label'), t('charts.value')]} rows={data.map(d => [formatPeriodLabel(d.label), fmt(d.value)])} />}
            {node}
        </Box>
    );
}
